import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './LandingPage.module.css';

/* ── 学习闭环 5 步骤 ── */
const STEPS = [
  {
    icon: 'menu_book',
    num: '01',
    title: '沉浸式双语阅读',
    subtitle: '中英对照，熟悉的故事降低认知负荷',
    text: '读你喜爱的中文小说英译版，剧情就是天然的理解支架。逐句对照，既读故事也学英文。',
  },
  {
    icon: 'search_check',
    num: '02',
    title: '一键查词 · 深度释义',
    subtitle: '点击即查，有道 + Collins 双词典',
    text: '阅读中遇到生词？点击即弹出音标、释义、例句。多词典融合，助你精准理解上下文。',
    reverse: true,
  },
  {
    icon: 'psychology',
    num: '03',
    title: 'AI 智能笔记',
    subtitle: '句子拆解、语法剖析、举一反三',
    text: 'AI 自动生成句型分析和重点词汇笔记，还能一键"举一反三"——把中文用多种风格翻译成英文，深度练习表达。',
  },
  {
    icon: 'headphones',
    num: '04',
    title: '真人级语音朗读',
    subtitle: '中英双语 TTS，边听边读',
    text: '每章包含纯英文、纯中文、先英后中、先中后英等多种音频模式。通勤路上也能"听小说学英语"。在中英的间隙思考并验证表达的正确性。',
    reverse: true,
  },
  {
    icon: 'school',
    num: '05',
    title: '卡片式复习 · 遮盖拉伸',
    subtitle: '单词 / 句子 / 笔记，多轮巩固增强记忆',
    text: '收藏的单词与句子自动进入复习系统。单词卡片翻转记忆、句子按比例遮盖填空、笔记分类回顾——用卡片式复习与遮盖拉伸学习，多轮巩固，强化长期记忆。',
  },
];

/* ── 核心功能卡片 ── */
const FEATURE_SECTIONS = [
  {
    id: 'reading',
    badge: '核心体验',
    icon: 'auto_stories',
    title: '中英对照 · 逐句阅读',
    desc: '每一句中文都有对应的英文翻译。点击任意单词即时查词，收藏生词和好句，构建你的专属词汇库。',
    highlights: ['逐句中英对照','多种阅读模式可选', '点击查词 · 多词典', '收藏单词 & 句子'],
    screenshotId: 'screenshot-reading',
    screenshotHint: '阅读界面截图',
  },
  {
    id: 'pdf',
    badge: 'PDF导出',
    icon: 'picture_as_pdf',
    title: 'PDF 导出',
    desc: '把章节和生词导出为精美 PDF，离线复习随时翻阅。打印或平板阅读都适用。',
    highlights: ['章节 + 生词一键导出', '精美排版 · 可打印', '离线携带 · 多设备阅读'],
    screenshotId: 'screenshot-pdf',
    screenshotHint: 'PDF 导出截图',
    reverse: true,
  },
  {
    id: 'dict',
    badge: '多字典+AI助记',
    icon: 'menu_book',
    title: '多字典查询',
    desc: '查词时支持 Youdao、Collins 等多词典切换，包含基础释义，例句，短语，同义词，反义词满足不同学习场景。AI助记可为单词生成联想助记内容，用故事、谐音、词根等方式加深印象，让背词更轻松。',
    highlights: ['Youdao · Collins 多源', '基础释义 + 例句 + 短语 + 同义词 + 反义词', '一键切换词典', 'AI助记'],
    screenshotId: 'screenshot-dict',
    screenshotHint: '多字典查询截图',
  },

  {
    id: 'ai',
    badge: 'AI 驱动',
    icon: 'psychology',
    title: 'AI 笔记 · 举一反三',
    desc: 'AI 自动生成每句的语法分析与重点词汇笔记。"举一反三"功能可将中文用文学、口语、简洁等多种英文风格翻译，全方位提升表达能力。',
    highlights: ['自动语法分析', '重点词汇提取', '多风格英文翻译'],
    screenshotId: 'screenshot-ai',
    screenshotHint: 'AI 笔记截图',
    reverse: true,
  },
  {
    id: 'audio',
    badge: '听力训练',
    icon: 'headphones',
    title: '四种音频模式 · 随时随地',
    desc: '每个章节提供纯英文、纯中文、先英后中、先中后英四种音频，搭配逐句高亮同步，让听力训练融入阅读。',
    highlights: ['EN / CN / EN→CN / CN→EN', '逐句高亮同步', '后台播放 · 通勤可用'],
    screenshotId: 'screenshot-audio',
    screenshotHint: '音频播放截图',
  },
  {
    id: 'study-word',
    badge: '复习系统',
    icon: 'menu_book',
    title: '单词复习',
    desc: '收藏的生词自动进入单词本。卡片翻转记忆、拼写练习、随机测试，用真实例句强化长期记忆。',
    highlights: ['单词卡片翻转','自动发音','拼写练习', '随机测试', '多轮复习'],
    screenshotId: 'screenshot-study-word',
    screenshotHint: '单词复习截图',
    reverse: true,
  },
  {
    id: 'study-sentence',
    badge: '复习系统',
    icon: 'format_quote',
    title: '句子复习',
    desc: '收藏的好句进入句子复习。遮盖填空、中英对照回忆，在语境中巩固表达。',
    highlights: ['句子按任意比例随机遮盖填空', '中英对照回忆', '按书/章节回顾'],
    screenshotId: 'screenshot-study-sentence',
    screenshotHint: '句子复习截图',
  },
  {
    id: 'study-note',
    badge: '复习系统',
    icon: 'edit_note',
    title: '笔记复习',
    desc: 'AI 笔记与手动笔记统一进入笔记复习。按书分类、按类型筛选，随时回顾重点。',
    highlights: ['笔记分类回顾', '按书/章节筛选', '重点一键复习'],
    screenshotId: 'screenshot-study-note',
    screenshotHint: '笔记复习截图',
    reverse: true,
  },
];

/* ── 截图资源（放在 public 下） ── */
const SCREENSHOTS = {
  hero: '/main.png',
  'screenshot-reading': '/chapterreader.png',
  'screenshot-pdf': '/pdf.png', // 配图放入 public 后改为 '/pdf_export.png' 等
  'screenshot-dict': "word_lookup.png", // 多字典查询配图
  'screenshot-ai': '/ainotes.png',
  'screenshot-audio': '/fourmp3.png',
  'screenshot-study-word': '/word_study.png',
  'screenshot-study-sentence': '/sentence_study.png',
  'screenshot-study-note': '/note_study.png',
};

/* ── 更多亮点 ── */
const MORE_FEATURES = [
  { icon: 'picture_as_pdf', title: 'PDF 导出', desc: '把章节和生词导出为精美 PDF，离线复习随时翻阅' },
  { icon: 'palette', title: '个性主题', desc: '12 款主题色 + 深浅模式，打造你的专属阅读空间' },
  { icon: 'trending_up', title: '阅读统计', desc: '追踪阅读进度、章节完成数、学习天数等成长数据' },
  { icon: 'group_add', title: '邀请好友', desc: '分享邀请码，双方各获 30 天 VIP，一起学习更有动力' },
  { icon: 'how_to_vote', title: '小说投票', desc: '想看什么书？投票告诉我们，票数最高的优先上架' },
  { icon: 'school', title: '助学计划', desc: '18 岁以下在校学生可申请免费终身VIP，学习无门槛' },
];

const LandingPage = () => {
  const navigate = useNavigate();

  const handleStart = () => navigate('/login');
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={styles.page}>
      {/* ════════ Navbar ════════ */}
      <header className={styles.navbar}>
        <div className={styles.navInner}>
          <div className={styles.logoWrap}>
            <div className={styles.logoBox}>
              <span className="material-symbols-outlined">auto_stories</span>
            </div>
            <span className={styles.logoText}>EnNovels</span>
          </div>

          <nav className={styles.navLinks}>
            <button type="button" className={styles.navLink} onClick={() => scrollTo('method')}>学习方法</button>
            <button type="button" className={styles.navLink} onClick={() => scrollTo('features')}>功能亮点</button>
            <button type="button" className={styles.navLink} onClick={() => scrollTo('more')}>更多功能</button>
          </nav>

          <button type="button" className={styles.ctaBtn} onClick={handleStart}>
            立即开始
          </button>
        </div>
      </header>

      <main className={styles.main}>
        {/* ════════ Hero ════════ */}
        <section className={styles.hero}>
          <div className={styles.heroBadge}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>bolt</span>
            用小说学英语，效率提升 3 倍
          </div>
          <h1 className={styles.heroTitle}>
            用你熟悉的中国小说
            <br />
            <span className={styles.heroTitleHighlight}>构建英语学习闭环</span>
          </h1>
          <p className={styles.heroDesc}>
            双语对照阅读 · AI 智能笔记 · 真人级语音 · 复习系统<br />
            一个应用覆盖"读、查、听、记、练"完整学习链路
          </p>
          <p className={styles.heroPlatform}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>devices</span>
            手机、电脑、iPad 全平台可用，无需下载软件
          </p>
          <div className={styles.heroPriceBanner}>
            <span className={styles.heroPriceTag}>推广价</span>
            <span className={styles.heroPriceSymbol}>¥</span>
            <span className={styles.heroPriceAmount}>78</span>
            <span className={styles.heroPriceInfo}>
              开通终身会员 · 一次付费永久使用
              <span className={styles.heroPriceWarn}>推广期结束后将恢复原价</span>
            </span>
          </div>
          <div className={styles.heroActions}>
            <button type="button" className={styles.heroCta} onClick={handleStart}>
              免费体验
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>arrow_forward</span>
            </button>
            <button type="button" className={styles.heroSecondary} onClick={() => scrollTo('method')}>
              了解学习方法
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>expand_more</span>
            </button>
          </div>

          {/* Hero 截图 */}
          <div className={styles.heroScreenshot} id="screenshot-hero">
            <img src={SCREENSHOTS.hero} alt="应用主界面" className={styles.screenshotImg} />
          </div>
        </section>

        {/* ════════ 学习闭环 5 步骤 ════════ */}
        <section id="method" className={styles.methodSection}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>学习方法</span>
            <h2 className={styles.sectionTitle}>五步闭环，高效内化</h2>
            <p className={styles.sectionSubtitle}>
              利用已知故事降低认知负荷，让英语学习变成一种享受
            </p>
          </div>

          <div className={styles.timeline}>
            <div className={styles.timelineLine} />
            {STEPS.map((step, i) => (
              <div key={i} className={`${styles.step} ${step.reverse ? styles.stepReverse : ''}`}>
                <div className={styles.stepLabel}>
                  <span className={styles.stepNum}>{step.num}</span>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepSubtitle}>{step.subtitle}</p>
                </div>
                <div className={styles.stepDot}>
                  <span className="material-symbols-outlined">{step.icon}</span>
                </div>
                <div className={styles.stepCard}>
                  <div className={styles.stepCardInner}>
                    <p className={styles.stepCardText}>{step.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ════════ 功能详细展示（含截图占位） ════════ */}
        <section id="features">
          {FEATURE_SECTIONS.map((feat, i) => (
            <div
              key={feat.id}
              className={`${styles.featureRow} ${feat.reverse ? styles.featureRowReverse : ''}`}
            >
              <div className={styles.featureText}>
                <span className={styles.featureBadge}>
                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{feat.icon}</span>
                  {feat.badge}
                </span>
                <h2 className={styles.featureTitle}>{feat.title}</h2>
                <p className={styles.featureDesc}>{feat.desc}</p>
                <ul className={styles.featureHighlights}>
                  {feat.highlights.map((h, j) => (
                    <li key={j} className={styles.featureHighlightItem}>
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.featureVisual} id={feat.screenshotId}>
                {feat.screenshots ? (
                  <div className={styles.studyScreenshotsGrid}>
                    {feat.screenshots.map((shot, k) => (
                      <img
                        key={k}
                        src={shot.src}
                        alt={shot.alt}
                        className={styles.screenshotImg}
                      />
                    ))}
                  </div>
                ) : SCREENSHOTS[feat.screenshotId] ? (
                  <img
                    src={SCREENSHOTS[feat.screenshotId]}
                    alt={feat.screenshotHint}
                    className={styles.screenshotImg}
                  />
                ) : (
                  <div className={styles.screenshotPlaceholder}>
                    <span className="material-symbols-outlined">{feat.icon}</span>
                    <span>{feat.screenshotHint}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </section>

        {/* ════════ 更多功能网格 ════════ */}
        <section id="more" className={styles.moreSection}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>更多功能</span>
            <h2 className={styles.sectionTitle}>不止于阅读</h2>
            <p className={styles.sectionSubtitle}>每一个细节，都为你的学习体验而设计</p>
          </div>
          <div className={styles.moreGrid}>
            {MORE_FEATURES.map((f, i) => (
              <div key={i} className={styles.moreCard}>
                <div className={styles.moreCardIcon}>
                  <span className="material-symbols-outlined">{f.icon}</span>
                </div>
                <h4 className={styles.moreCardTitle}>{f.title}</h4>
                <p className={styles.moreCardDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ════════ 数据亮点 ════════ */}
        <section className={styles.statsSection}>
          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>50+</span>
              <span className={styles.statLabel}>经典中文小说</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <span className={styles.statNumber}>5000+</span>
              <span className={styles.statLabel}>章节双语内容</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <span className={styles.statNumber}>4 种</span>
              <span className={styles.statLabel}>音频播放模式</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <span className={styles.statNumber}>12 款</span>
              <span className={styles.statLabel}>个性主题色</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <span className={styles.statNumber}>每周 1 部</span>
              <span className={styles.statLabel}>新小说更新</span>
            </div>
          </div>
        </section>

        {/* ════════ 底部 CTA ════════ */}
        <section className={styles.finalCta}>
          <div className={styles.finalCtaGlow} />
          <span className={`material-symbols-outlined ${styles.finalCtaQuote}`}>format_quote</span>
          <h2 className={styles.finalCtaTitle}>
            因为剧情熟悉，理解难度大幅降低
          </h2>
          <p className={styles.finalCtaSubtitle}>
            熟悉剧情 · 无感阅读 · 快速掌握
          </p>
          <div className={styles.finalCtaPriceBanner}>
            <span className={styles.finalCtaPriceTag}>限时推广价</span>
            <div className={styles.finalCtaPriceRow}>
              <span className={styles.finalCtaPriceSymbol}>¥</span>
              <span className={styles.finalCtaPriceAmount}>78</span>
              <span className={styles.finalCtaPriceLabel}>开通终身会员</span>
            </div>
            <span className={styles.finalCtaPriceWarn}>推广期结束后将恢复原价</span>
          </div>
          <button type="button" className={styles.finalCtaBtn} onClick={handleStart}>
            立即开启你的学习闭环
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>arrow_forward</span>
          </button>
          <p className={styles.finalCtaHint}>新注册用户免费体验 7 天 VIP</p>
        </section>
      </main>

      {/* ════════ Footer ════════ */}
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerLogo}>
            <div className={styles.footerLogoBox}>
              <span className="material-symbols-outlined">auto_stories</span>
            </div>
            <span className={styles.footerLogoText}>EnNovels</span>
          </div>
          <div className={styles.footerLinks}>
            <button type="button" className={styles.footerLink} onClick={() => navigate('/terms')}>隐私政策</button>
            <button type="button" className={styles.footerLink} onClick={() => navigate('/terms')}>服务条款</button>
          </div>
          <p className={styles.footerCopy}>© 2025 EnNovels Education Inc. 保留所有权利。</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
